import "./Modals.scss";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Company, Department, ModalProps, User } from "../../utils/types";
import {
  fetchData,
  getErrorMessage,
  Notify,
  toDropdownListFormat,
} from "../../utils/constants";
import { rehireEmployee } from "../../utils/employmentService";
import SearchInput from "../Search/SearchInput";

type RehireModalProps = ModalProps & {
  user: User;
  refreshData: () => Promise<void>;
};

const fullName = (u: User | null) => (u ? `${u.firstName} ${u.lastName}` : "");

const findByFullName = (users: User[], name: string | null) => {
  if (!name) return null;
  const target = name.toLowerCase().trim();
  return (
    users.find(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().trim() === target ||
        `${u.lastName} ${u.firstName}`.toLowerCase().trim() === target,
    ) || null
  );
};

// New employment for an existing person (same email): re-hire in the same
// company or hire in another company of the group. Starts from zero - the
// current year days are entered manually, nothing is carried over.
const RehireModal = ({
  isOpen,
  setIsOpen,
  user,
  refreshData,
}: RehireModalProps) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [company, setCompany] = useState<number | "">("");
  const [department, setDepartment] = useState<number | "">("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [workHours, setWorkHours] = useState<number>(8);
  const [entitlement, setEntitlement] = useState<number | null>(null);
  const [currentYearDays, setCurrentYearDays] = useState<number | null>(null);
  const [manager, setManager] = useState<User | null>(null);
  const [managerSearchValue, setManagerSearchValue] = useState("");
  const [timekeeper, setTimekeeper] = useState<User | null>(null);
  const [timekeeperSearchValue, setTimekeeperSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLists = useCallback(async () => {
    await Promise.all([
      fetchData(
        `${process.env.REACT_APP_API_URL}/companies/getAllCompanies`,
        setCompanies,
      ),
      fetchData(
        `${process.env.REACT_APP_API_URL}/departments/allAllDepartments`,
        setDepartments,
      ),
      fetchData(
        `${process.env.REACT_APP_API_URL}/users/displayAllUsers`,
        setUsers,
      ),
    ]);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadLists();
  }, [isOpen, loadLists]);

  // Prefill from the previous employment; the employee code stays empty on
  // purpose (a new employment gets its own code).
  useEffect(() => {
    if (!isOpen) return;
    setStartDate(null);
    setEmployeeCode("");
    setJobTitle(user.jobTitle || "");
    setWorkHours(user.workHours || 8);
    setEntitlement(user.entitlement ?? null);
    setCurrentYearDays(null);
    setError(null);
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;
    const previousCompany = companies.find((c) => c.name === user.companyName);
    setCompany(previousCompany ? previousCompany.id : "");
  }, [isOpen, companies, user.companyName]);

  useEffect(() => {
    if (!isOpen) return;
    const previousDepartment = departments.find(
      (d) => d.nameDepartment === user.departmentName,
    );
    setDepartment(previousDepartment ? previousDepartment.id : "");
  }, [isOpen, departments, user.departmentName]);

  useEffect(() => {
    if (!isOpen) return;
    const previousManager = findByFullName(users, user.managerFullName);
    setManager(previousManager);
    setManagerSearchValue(fullName(previousManager));
    const previousTimekeeper = findByFullName(users, user.timekeeperFullName);
    setTimekeeper(previousTimekeeper);
    setTimekeeperSearchValue(fullName(previousTimekeeper));
  }, [isOpen, users, user.managerFullName, user.timekeeperFullName]);

  const previousEndDate = user.employmentEndDate
    ? dayjs(user.employmentEndDate)
    : null;

  const handleSubmit = async () => {
    if (
      !startDate ||
      !company ||
      !employeeCode.trim() ||
      entitlement == null ||
      currentYearDays == null
    ) {
      Notify(
        "error",
        "Start date, company, employee code, entitlement and current year days are required.",
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await rehireEmployee(user.id, {
        startDate: startDate.format("YYYY-MM-DD"),
        companyId: company,
        employeeCode: employeeCode.trim(),
        currentYearDays,
        entitlement,
        departmentId: department || null,
        managerId: manager?.id ?? null,
        timekeeperId: timekeeper?.id ?? null,
        jobTitle,
        workHours,
      });
      Notify(
        "success",
        `New employment registered for ${user.firstName} ${user.lastName} from ${startDate.format("DD.MM.YYYY")}.`,
      );
      setIsOpen(false);
      await refreshData();
    } catch (err) {
      setError(
        getErrorMessage(err, "Oops! Something went wrong while rehiring."),
      );
    } finally {
      setSaving(false);
    }
  };

  const otherUsers = users.filter((u) => u.id !== user.id);

  return (
    <Modal
      className="lg"
      width={760}
      open={isOpen}
      title={`Rehire / new employment - ${user.firstName} ${user.lastName}`}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={saving}
          onClick={handleSubmit}
        >
          Register employment
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary">
        The new employment starts from zero: leave days left from the previous
        employment are not shown or carried over. The employee gets access
        again from the start date.
        {previousEndDate &&
          ` Previous employment ended on ${previousEndDate.format("DD.MM.YYYY")}.`}
      </Typography.Paragraph>
      <Form layout="vertical" requiredMark>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Start date" required>
              <DatePicker
                format="DD.MM.YYYY"
                value={startDate}
                onChange={(value) => setStartDate(value)}
                disabledDate={(date) =>
                  !!previousEndDate && !date.isAfter(previousEndDate, "day")
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Company" required>
              <Select
                value={company}
                options={toDropdownListFormat(companies, "name", "id").map(
                  (item) => ({ label: item.text, value: item.value }),
                )}
                onChange={(value) => setCompany(Number(value))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="New employee code"
              required
              tooltip="Unique within the company."
            >
              <Input
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Department">
              <Select
                value={department}
                options={toDropdownListFormat(
                  departments,
                  "nameDepartment",
                  "id",
                ).map((item) => ({ label: item.text, value: item.value }))}
                onChange={(value) => setDepartment(Number(value))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Manager">
              <SearchInput
                onSelect={(u) => {
                  setManager(u);
                  setManagerSearchValue(fullName(u));
                }}
                options={otherUsers}
                toDisplay={["firstName", "lastName"]}
                value={managerSearchValue}
                onChangeValue={setManagerSearchValue}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Timekeeper">
              <SearchInput
                onSelect={(u) => {
                  setTimekeeper(u);
                  setTimekeeperSearchValue(fullName(u));
                }}
                options={otherUsers}
                toDisplay={["firstName", "lastName"]}
                value={timekeeperSearchValue}
                onChangeValue={setTimekeeperSearchValue}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Job Title">
              <Input
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Working Hours">
              <InputNumber
                min={0}
                value={workHours}
                onChange={(value) =>
                  setWorkHours(typeof value === "number" ? value : 0)
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Entitlement" required>
              <InputNumber
                min={0}
                value={entitlement}
                onChange={(value) =>
                  setEntitlement(typeof value === "number" ? value : null)
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Current Year Days"
              required
              tooltip="Days for the start year of this employment, entered manually."
            >
              <InputNumber
                min={0}
                value={currentYearDays}
                onChange={(value) =>
                  setCurrentYearDays(typeof value === "number" ? value : null)
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
};

export default RehireModal;
